import { useEffect, useState } from 'react';
import { Alert, Avatar, Box, Skeleton, Typography } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import { BrandIcon } from '@components/BrandIcon';
import facebook from '@iconify-icons/logos/facebook';
import MDButton from '@components/MDButton';
import { TabBar } from '@components/TabBar';
import { useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { formatCrmDateTime } from '@layouts/pages/CRM/kanban-board/formatters/crm-datetime';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { Integration, IntegrationService } from '../services/integration.service';
import { LeadFormsConnection, LeadFormsService } from '../services/lead-forms.service';
import { IntegrationFields } from '../components/integration-fields';
import { LeadFormsWizard } from '../components/lead-forms-wizard';
import { INTEGRATIONS_ROUTE, IntegrationBackButton } from '../components/integration-back-button';

const META_GROUP = 'meta';

/// Tela da conta da Meta: uma conexão no topo e uma aba por integração que a usa.
export const MetaIntegrations = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [integrations, setIntegrations] = useState<Integration[] | null>(null);

    /// Perfil do Facebook; conexão antiga não o guardou, e buscar ao vivo evita pedir reconexão.
    const [profile, setProfile] = useState<{ name?: string | null; picture?: string | null }>();

    /// O que a integração de formulários usa hoje; vem do serviço dela, não do catálogo.
    const [forms, setForms] = useState<LeadFormsConnection | null>();

    /// O assistente de formulários roda dentro da aba, no lugar do resumo.
    const [wizard, setWizard] = useState(false);

    const load = () =>
        new IntegrationService()
            .list()
            .then((res) => setIntegrations(
                (res.data.data ?? []).filter((item) => item.groupKey === META_GROUP)))
            .catch(() => setIntegrations([]));

    const loadForms = () =>
        new LeadFormsService()
            .getConnection()
            .then((res) => setForms(res.data.data ?? null))
            .catch(() => setForms(null));

    useEffect(() => {
        load();
        loadForms();
    }, []);

    const connected = integrations?.find((item) => item.connectedAt);
    const saved = integrations?.find((item) => item.connectedAccountName);

    useEffect(() => {
        if (saved || !connected) return;

        new IntegrationService()
            .metaAccount()
            .then((res) => setProfile(res.data.data ?? undefined))
            // Sem perfil a tela cai nas iniciais; não vale um alerta.
            .catch(() => setProfile(undefined));
    }, [Boolean(saved), Boolean(connected)]);

    // A conexão com a Meta abre outra aba; ao voltar a esta, reconferir mostra a conta conectada.
    useEffect(() => {
        if (!integrations || connected) return;

        const onBack = () => {
            load();
            loadForms();
        };

        window.addEventListener('focus', onBack);

        return () => window.removeEventListener('focus', onBack);
    }, [integrations, Boolean(connected)]);

    const group = integrations?.[0];
    const current = integrations?.find((item) => item.key === searchParams.get('aba')) ?? group;

    const changeTab = (key: string) => {
        setWizard(false);
        setSearchParams({ aba: key }, { replace: true });
    };

    /// Volta para de onde veio; aberto por link, sem tela anterior, vai à lista de integrações.
    const goBack = () => {
        if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
        else navigate(INTEGRATIONS_ROUTE);
    };

    const connect = async () => {
        try {
            const res = await new IntegrationService().metaConnectUrl();

            if (!res.data.data.ready) {
                showAlert(
                    res.data.data.reason ?? t('integracoes.naoConfiguravel'), 'warning', 6000);
                return;
            }

            // Aba nova: o consentimento acontece no domínio da Meta, e sair perderia o preenchido.
            window.open(res.data.data.url!, '_blank', 'noopener');
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    const disconnect = async () => {
        if (!connected) return;

        try {
            await new IntegrationService().disconnect(connected.key);
            showAlert(t('integracoes.desconectada'), 'success', 2500);
            setProfile(undefined);
            load();
            loadForms();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    const accountName = connected?.connectedAccountName ?? profile?.name ?? null;
    const accountPhoto = connected?.connectedAccountPicture ?? profile?.picture ?? null;

    const initials = (accountName ?? '')
        .split(' ').filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');

    return (
        <DashboardLayout>
            <DashboardNavbar title={group?.groupName ?? t('sideNav.configuracoes.integracoes')} />

            <Box sx={{ background: cc.pageBg, borderRadius: 3, p: 2 }}>
                <IntegrationBackButton />

                {!integrations || !current ? (
                    <Box>
                        <Skeleton variant="rounded" height={72} sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={280} />
                    </Box>
                ) : (
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{ color: cc.textSecondary, display: 'block', mb: 1.5 }}
                        >
                            {group.groupDescription}
                        </Typography>

                        {/* A conexão vem antes das abas: todas a compartilham e sem ela nenhuma lista carrega. */}
                        {connected ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    border: `1px solid ${cc.border}`,
                                    borderRadius: '10px',
                                    bgcolor: cc.countBg,
                                }}
                            >
                                <Avatar
                                    src={accountPhoto ?? undefined}
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        bgcolor: cc.accentSoftBg,
                                        color: cc.onTint,
                                        '& .MuiSvgIcon-root': { color: cc.accent },
                                    }}
                                >
                                    {initials || <BrandIcon icon={facebook} size={22} />}
                                </Avatar>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 700, color: cc.textPrimary }}
                                    >
                                        {accountName ?? t('integracoes.contaSemNome')}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', color: cc.textSecondary }}
                                    >
                                        {t('integracoes.conectadoEm', {
                                            quando: formatCrmDateTime(connected.connectedAt!),
                                        })}
                                        {connected.connectedByUserName
                                            ? ` · ${connected.connectedByUserName}`
                                            : ''}
                                    </Typography>
                                </Box>

                                <MDButton variant="text" color="dark" size="small" onClick={connect}>
                                    {t('integracoes.reconectar')}
                                </MDButton>
                                <MDButton variant="text" color="error" size="small" onClick={disconnect}>
                                    {t('integracoes.desconectar')}
                                </MDButton>
                            </Box>
                        ) : (
                            <Box>
                                <Alert severity="info" icon={false} sx={{ mb: 1.5 }}>
                                    <Typography variant="caption">
                                        {t('integracoes.conecteAjuda')}
                                    </Typography>
                                </Alert>
                                <MDButton
                                    variant="gradient"
                                    color="info"
                                    startIcon={<FacebookIcon />}
                                    onClick={connect}
                                >
                                    {t('integracoes.conectarFacebook')}
                                </MDButton>
                            </Box>
                        )}

                        <Box sx={{ mt: 2 }}>
                            <TabBar
                                current={current.key}
                                onChange={changeTab}
                                tabs={integrations.map((item) => ({ value: item.key, label: item.name }))}
                            />
                        </Box>

                        <Typography
                            variant="caption"
                            sx={{ color: cc.textSecondary, display: 'block', mt: 1.5, mb: 2 }}
                        >
                            {current.description}
                        </Typography>

                        {/* Formulários tem fluxo próprio (página e formulário): campos soltos não dariam conta. */}
                        {current.key === 'meta-lead-ads' ? (
                            forms === undefined ? (
                                <Skeleton height={64} variant="rounded" />
                            ) : !forms?.connected ? (
                                <Alert severity="info" icon={false}>
                                    <Typography variant="caption">
                                        {t('integracoes.formulariosConecteAntes')}
                                    </Typography>
                                </Alert>
                            ) : wizard ? (
                                <LeadFormsWizard
                                    connection={forms}
                                    onClose={() => setWizard(false)}
                                    onSaved={() => { loadForms(); load(); }}
                                />
                            ) : (
                                // Mesmo bloco da conta: com ou sem formulário, a ação fica no mesmo lugar.
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.5,
                                        border: `1px ${forms.pageId ? 'solid' : 'dashed'} ${cc.border}`,
                                        borderRadius: '10px',
                                        bgcolor: cc.countBg,
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 42,
                                            height: 42,
                                            bgcolor: cc.accentSoftBg,
                                            color: cc.accent,
                                        }}
                                    >
                                        <FactCheckOutlinedIcon />
                                    </Avatar>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 700, color: cc.textPrimary }}
                                            noWrap
                                        >
                                            {forms.pageId
                                                ? forms.formName ?? t('integracoes.paginaInteira')
                                                : t('integracoes.formulariosSemConfigurar')}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ display: 'block', color: cc.textSecondary }}
                                        >
                                            {forms.pageId
                                                ? t('integracoes.naPagina', { pagina: forms.pageName ?? '—' })
                                                : t('integracoes.formulariosAjuda')}
                                        </Typography>
                                    </Box>

                                    <MDButton
                                        variant={forms.pageId ? 'text' : 'gradient'}
                                        color={forms.pageId ? 'dark' : 'info'}
                                        size="small"
                                        onClick={() => setWizard(true)}
                                    >
                                        {forms.pageId
                                            ? t('integracoes.trocarFormulario')
                                            : t('integracoes.conectarFormulario')}
                                    </MDButton>
                                </Box>
                            )
                        ) : (
                            <IntegrationFields
                                key={current.key}
                                integration={current}
                                onSaved={load}
                                onClose={goBack}
                                hideConnection
                            />
                        )}
                    </Box>
                )}
            </Box>
        </DashboardLayout>
    );
};
